/**
 * Centralized rate limit error detection and handling
 */

/**
 * Supabase Functions can return errors with context containing status codes
 */
export interface SupabaseFunctionError {
  message?: string;
  context?: {
    status?: number;
  };
}

/**
 * Union type for errors we might encounter
 */
type ErrorType = Error | SupabaseFunctionError | unknown;

interface RateLimitErrorResult {
  isRateLimitError: boolean;
  isAuthError: boolean;
  isValidationError: boolean;
  errorMessage: string;
}

/**
 * Detect if an error is a rate limit error (429 status)
 * Works with Supabase Functions errors and generic Error objects
 */
export function detectErrorType(error: ErrorType): RateLimitErrorResult {
  const errorMessage =
    error &&
    typeof error === 'object' &&
    'message' in error &&
    typeof error.message === 'string'
      ? error.message
      : error != null
        ? String(error)
        : '';

  const errorContext =
    error && typeof error === 'object' && 'context' in error
      ? (error as SupabaseFunctionError).context
      : undefined;

  const lowerErrorMessage = errorMessage.toLowerCase();

  const isRateLimitError =
    errorContext?.status === 429 ||
    /\b429\b/.test(errorMessage) ||
    lowerErrorMessage.includes('rate limit');

  const isAuthError =
    errorContext?.status === 401 ||
    errorContext?.status === 403 ||
    /\b401\b/.test(errorMessage) ||
    /\b403\b/.test(errorMessage);

  const isValidationError =
    errorContext?.status === 400 || /\b400\b/.test(errorMessage);

  return {
    isRateLimitError,
    isAuthError,
    isValidationError,
    errorMessage,
  };
}

/**
 * Get user-friendly error messages based on error type
 */
export function getRateLimitErrorMessages(context: string = 'requests') {
  return {
    title: 'Rate limit reached',
    description: `You've made too many ${context} in a short period. Please wait and try again soon.`,
  };
}

/**
 * Get generic error messages for non-rate-limit errors
 */
export function getGenericErrorMessages(
  action: string = 'process your request'
) {
  return {
    title: 'Error',
    description: `Unable to ${action}. Please try again.`,
  };
}

/**
 * Centralized error message selector
 */
export function getErrorMessages(
  error: ErrorType,
  context: {
    rateLimitContext?: string; // e.g., "messages", "analyses", "modules"
    genericAction?: string; // e.g., "analyze your product", "generate module"
  } = {}
): { title: string; description: string } {
  const { isRateLimitError } = detectErrorType(error);

  if (isRateLimitError) {
    return getRateLimitErrorMessages(context.rateLimitContext || 'requests');
  }

  return getGenericErrorMessages(
    context.genericAction || 'process your request'
  );
}
